import React from 'react';
import { Link } from 'react-router-dom';
import { 
  RocketLaunchIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PhotoIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  TagIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const HelpPage: React.FC = () => {
  const steps = [
    {
      icon: <DocumentTextIcon className="h-8 w-8" />,
      title: "ایجاد حساب کاربری",
      description: "ابتدا در سایت ثبت نام کنید یا به حساب کاربری خود وارد شوید. این مرحله برای تأیید هویت و امنیت آگهی ضروری است."
    },
    {
      icon: <TagIcon className="h-8 w-8" />,
      title: "انتخاب دسته‌بندی",
      description: "دسته‌بندی مناسب برای آگهی خود را انتخاب کنید. این کار به خریداران کمک می‌کند آگهی شما را راحت‌تر پیدا کنند."
    },
    {
      icon: <DocumentTextIcon className="h-8 w-8" />,
      title: "تکمیل اطلاعات",
      description: "یک عنوان جذاب، توضیحات کامل و مشخصات فنی محصول یا خدمات خود را به دقت وارد کنید."
    },
    {
      icon: <PhotoIcon className="h-8 w-8" />,
      title: "افزودن تصاویر",
      description: "تصاویر با کیفیت و واضح از محصول خود آپلود کنید. تصاویر خوب تأثیر قابل توجهی در جذب خریداران دارند."
    },
    {
      icon: <CurrencyDollarIcon className="h-8 w-8" />,
      title: "تعیین قیمت",
      description: "قیمت معقول و رقابتی برای محصول یا خدمات خود تعیین کنید. همچنین می‌توانید قیمت را قابل مذاکره اعلام کنید."
    },
    {
      icon: <MapPinIcon className="h-8 w-8" />,
      title: "افزودن مکان",
      description: "مکان دقیق محصول یا خدمات خود را وارد کنید. این اطلاعات به خریداران کمک می‌کند شما را راحت‌تر پیدا کنند."
    }
  ];

  const tips = [
    {
      icon: <CheckCircleIcon className="h-6 w-6 text-green-500" />,
      title: "نکات موفقیت",
      items: [
        "از تصاویر واضح و با کیفیت استفاده کنید",
        "توضیحات کامل و دقیق بنویسید",
        "قیمت‌های رقابتی تعیین کنید",
        "به سرعت به پیام‌ها پاسخ دهید",
        "اطلاعات تماس خود را به‌روز نگه دارید"
      ]
    },
    {
      icon: <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />,
      title: "نکات مهم",
      items: [
        "از ارسال آگهی‌های تکراری خودداری کنید",
        "اطلاعات شخصی را در توضیحات قرار ندهید",
        "از دسته‌بندی‌های مناسب استفاده کنید",
        "قوانین و مقررات سایت را رعایت کنید",
        "آگهی‌های مشکوک را گزارش دهید"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700">
                    خانه
                </Link>
              </li>
              <li>
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              </li>
              <li>
                <span className="text-gray-900 font-medium">مرکز راهنمایی</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>  

      {/* How to Post Ads Section */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full mb-6">
              <DocumentTextIcon className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 mb-6">نحوه ثبت آگهی</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              مراحل ساده برای ثبت آگهی‌های مؤثر و جذاب که به شما کمک می‌کند بهترین نتایج را بگیرید
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="group relative">
                {/* Connection line for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-[-2rem] w-16 h-0.5 bg-gradient-to-r from-red-200 to-red-300 z-0" />
                )}
                
                <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group-hover:border-red-200">
                  {/* Step number badge */}
                  <div className="absolute -top-4 right-6">
                    <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white text-sm font-semibold">{index + 1}</span>
                    </div>
                  </div>
                  
                  {/* Icon */}
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-50 to-red-100 rounded-2xl mb-6 group-hover:from-red-100 group-hover:to-red-200 transition-colors duration-300">
                    <div className="text-red-600 group-hover:text-red-700 transition-colors duration-300">
                      {step.icon}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-red-700 transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-base">
                    {step.description}
                  </p>
                  
                  {/* Decorative element */}
                  <div className="absolute bottom-4 left-6 w-12 h-1 bg-gradient-to-r from-red-200 to-red-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>
          
          {/* Call to action */}
          <div className="text-center mt-16">
            <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
              <span className="text-gray-700 font-medium">آماده شروع هستید؟</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {tips.map((tip, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  {tip.icon}
                  <h3 className="text-lg font-semibold text-gray-900 mr-3">{tip.title}</h3>
                </div>
                <ul className="space-y-2">
                  {tip.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">آماده ثبت آگهی هستید؟</h2>
          <p className="text-xl text-primary-100 mb-8">
            همین حالا شروع کنید و محصولات یا خدمات خود را بفروشید
          </p>
          <Link
            to="/post-ad"
            className="inline-flex items-center bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors duration-300 shadow-lg"
          >
            <DocumentTextIcon className="h-6 w-6 ml-3" />
            ثبت آگهی جدید
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HelpPage; 